import { StyleType, CssColor, wixColor } from '@wix/editor-platform-ooi-metadata-resolver';
import { StyleParamType, wixColorParam, wixFontParam, IWixStyleFont, createStylesParam } from '@wix/tpa-settings';
import { Alignment } from '../../utils/alignment';
import { StyleParam } from './stylesParams';

function deriveHighlighted<T extends StyleParamType>(regular: StyleParam<T>): StyleParam<T> {
  return {
    ...regular,
    key: regular.key + '-highlighted',
    getDefaultValue: (params) =>
      params.getStyleParamValue(regular) || (regular.getDefaultValue && regular.getDefaultValue(params)),
  };
}

function deriveHovered<T extends StyleParamType>(regular: StyleParam<T>): StyleParam<T> {
  return {
    ...regular,
    key: regular.key + '-hovered',
    getDefaultValue: (params) =>
      params.getStyleParamValue(regular) || (regular.getDefaultValue && regular.getDefaultValue(params)),
  };
}

/** ButtonStyle.FULL_CORNERED (DEFAULT)
 *
 * Editor 3 will not have button style selection, so metadata is defined only for the default style.
 */
const FULL_CORNERED_PARAMS = {
  buttonFullCorneredFontStyle: createStylesParam('full-cornered-font-style', {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
      return { ...font } as IWixStyleFont;
    },
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-M',
    //     size: 14,
    //   },
    // },
  }),
  buttonFullCorneredWidth: createStylesParam('full-cornered-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 262,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 262,
      },
    },
  }),
  buttonFullCorneredAlignment: createStylesParam('full-cornered-alignment', {
    type: StyleParamType.Number,
    getDefaultValue: () => Alignment.CENTER,
    metadata: {
      // TODO: Check how alignment works in Editor 3 and adjust accordingly
      type: StyleType.justifyContent,
      defaultValue: {
        type: 'static',
        value: 'center',
      },
    },
  }),
  buttonFullCorneredFontColor: createStylesParam('full-cornered-font-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstButtonPrimaryColor),
    },
  }),
  buttonFullCorneredBorderOpacityAndColor: createStylesParam('full-cornered-border-opacity-and-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstButtonPrimaryBorderLeftColor),
    },
  }),
  buttonFullCorneredBorderWidth: createStylesParam('full-cornered-border-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  buttonFullCorneredCornerRadius: createStylesParam('full-cornered-corner-radius', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  buttonFullCorneredBackground: createStylesParam('full-cornered-bg-opacity-and-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstButtonPrimaryBackgroundColor),
    },
  }),
};

const FULL_CORNERED_PARAMS_HIGHLIGHTED = {
  buttonFullCorneredFontColorHighlighted: deriveHighlighted(FULL_CORNERED_PARAMS.buttonFullCorneredFontColor),
  buttonFullCorneredCornerRadiusHighlighted: deriveHighlighted(FULL_CORNERED_PARAMS.buttonFullCorneredCornerRadius),
  buttonFullCorneredBorderOpacityAndColorHighlighted: deriveHighlighted(
    FULL_CORNERED_PARAMS.buttonFullCorneredBorderOpacityAndColor,
  ),
  buttonFullCorneredBackgroundHighlighted: deriveHighlighted(FULL_CORNERED_PARAMS.buttonFullCorneredBackground),
};

const FULL_CORNERED_PARAMS_HOVERED = {
  buttonFullCorneredFontColorHovered: deriveHovered(FULL_CORNERED_PARAMS.buttonFullCorneredFontColor),
  buttonFullCorneredBorderOpacityAndColorHovered: deriveHovered(
    FULL_CORNERED_PARAMS.buttonFullCorneredBorderOpacityAndColor,
  ),
  buttonFullCorneredBackgroundHovered: deriveHovered(FULL_CORNERED_PARAMS.buttonFullCorneredBackground),

  buttonFullCorneredFontColorHighlightedHovered: deriveHovered(
    FULL_CORNERED_PARAMS_HIGHLIGHTED.buttonFullCorneredFontColorHighlighted,
  ),
  buttonFullCorneredBorderOpacityAndColorHighlightedHovered: deriveHovered(
    FULL_CORNERED_PARAMS_HIGHLIGHTED.buttonFullCorneredBorderOpacityAndColorHighlighted,
  ),
  buttonFullCorneredBackgroundHighlightedHovered: deriveHovered(
    FULL_CORNERED_PARAMS_HIGHLIGHTED.buttonFullCorneredBackgroundHighlighted,
  ),
};

/** ButtonStyle.FULL_ROUNDED */
const FULL_ROUNDED_PARAMS = {
  buttonFullRoundedFontStyle: createStylesParam('full-rounded-font-style', {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
      return { ...font } as IWixStyleFont;
    },
  }),
  buttonFullRoundedWidth: createStylesParam('full-rounded-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 262,
  }),
  buttonFullRoundedAlignment: createStylesParam('full-rounded-alignment', {
    type: StyleParamType.Number,
    getDefaultValue: () => Alignment.CENTER,
  }),
  buttonFullRoundedFontColor: createStylesParam('full-rounded-font-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstButtonPrimaryColor),
    },
  }),
  buttonFullRoundedBorderOpacityAndColor: createStylesParam('full-rounded-border-opacity-and-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstButtonPrimaryBorderLeftColor),
    },
  }),
  buttonFullRoundedBorderWidth: createStylesParam('full-rounded-border-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  }),
  buttonFullRoundedCornerRadius: createStylesParam('full-rounded-corner-radius', {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
  }),
  buttonFullRoundedBackground: createStylesParam('full-rounded-bg-opacity-and-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstButtonPrimaryBackgroundColor),
    },
  }),
};

const FULL_ROUNDED_PARAMS_HIGHLIGHTED = {
  buttonFullRoundedFontColorHighlighted: deriveHighlighted(FULL_ROUNDED_PARAMS.buttonFullRoundedFontColor),
  buttonFullRoundedCornerRadiusHighlighted: deriveHighlighted(FULL_ROUNDED_PARAMS.buttonFullRoundedCornerRadius),
  buttonFullRoundedBorderOpacityAndColorHighlighted: deriveHighlighted(
    FULL_ROUNDED_PARAMS.buttonFullRoundedBorderOpacityAndColor,
  ),
  buttonFullRoundedBackgroundHighlighted: deriveHighlighted(FULL_ROUNDED_PARAMS.buttonFullRoundedBackground),
};

const FULL_ROUNDED_PARAMS_HOVERED = {
  buttonFullRoundedFontColorHovered: deriveHovered(FULL_ROUNDED_PARAMS.buttonFullRoundedFontColor),
  buttonFullRoundedBorderOpacityAndColorHovered: deriveHovered(
    FULL_ROUNDED_PARAMS.buttonFullRoundedBorderOpacityAndColor,
  ),
  buttonFullRoundedBackgroundHovered: deriveHovered(FULL_ROUNDED_PARAMS.buttonFullRoundedBackground),

  buttonFullRoundedFontColorHighlightedHovered: deriveHovered(
    FULL_ROUNDED_PARAMS_HIGHLIGHTED.buttonFullRoundedFontColorHighlighted,
  ),
  buttonFullRoundedBorderOpacityAndColorHighlightedHovered: deriveHovered(
    FULL_ROUNDED_PARAMS_HIGHLIGHTED.buttonFullRoundedBorderOpacityAndColorHighlighted,
  ),
  buttonFullRoundedBackgroundHighlightedHovered: deriveHovered(
    FULL_ROUNDED_PARAMS_HIGHLIGHTED.buttonFullRoundedBackgroundHighlighted,
  ),
};

/** ButtonStyle.EMPTY_CORNERED */
const EMPTY_CORNERED_PARAMS = {
  buttonEmptyCorneredFontStyle: createStylesParam('empty-cornered-font-style', {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
      return { ...font } as IWixStyleFont;
    },
  }),
  buttonEmptyCorneredWidth: createStylesParam('empty-cornered-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 262,
  }),
  buttonEmptyCorneredAlignment: createStylesParam('empty-cornered-alignment', {
    type: StyleParamType.Number,
    getDefaultValue: () => Alignment.CENTER,
  }),
  buttonEmptyCorneredFontColor: createStylesParam('empty-cornered-font-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstButtonPrimaryColor),
    },
  }),
  buttonEmptyCorneredBorderWidth: createStylesParam('empty-cornered-border-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  }),
  buttonEmptyCorneredCornerRadius: createStylesParam('empty-cornered-corner-radius', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  }),
  buttonEmptyCorneredBorderOpacityAndColor: createStylesParam('empty-cornered-border-opacity-and-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstButtonPrimaryBorderLeftColor),
    },
  }),
  buttonEmptyCorneredBackground: createStylesParam('empty-cornered-bg-opacity-and-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstButtonPrimaryBackgroundColor),
    },
  }),
};

const EMPTY_CORNERED_PARAMS_HIGHLIGHTED = {
  buttonEmptyCorneredFontColorHighlighted: deriveHighlighted(EMPTY_CORNERED_PARAMS.buttonEmptyCorneredFontColor),
  buttonEmptyCorneredCornerRadiusHighlighted: deriveHighlighted(EMPTY_CORNERED_PARAMS.buttonEmptyCorneredCornerRadius),
  buttonEmptyCorneredBorderOpacityAndColorHighlighted: deriveHighlighted(
    EMPTY_CORNERED_PARAMS.buttonEmptyCorneredBorderOpacityAndColor,
  ),
  buttonEmptyCorneredBackgroundHighlighted: deriveHighlighted(EMPTY_CORNERED_PARAMS.buttonEmptyCorneredBackground),
};

const EMPTY_CORNERED_PARAMS_HOVERED = {
  buttonEmptyCorneredFontColorHovered: deriveHovered(EMPTY_CORNERED_PARAMS.buttonEmptyCorneredFontColor),
  buttonEmptyCorneredBorderOpacityAndColorHovered: deriveHovered(
    EMPTY_CORNERED_PARAMS.buttonEmptyCorneredBorderOpacityAndColor,
  ),
  buttonEmptyCorneredBackgroundHovered: deriveHovered(EMPTY_CORNERED_PARAMS.buttonEmptyCorneredBackground),

  buttonEmptyCorneredFontColorHighlightedHovered: deriveHovered(
    EMPTY_CORNERED_PARAMS_HIGHLIGHTED.buttonEmptyCorneredFontColorHighlighted,
  ),
  buttonEmptyCorneredBorderOpacityAndColorHighlightedHovered: deriveHovered(
    EMPTY_CORNERED_PARAMS_HIGHLIGHTED.buttonEmptyCorneredBorderOpacityAndColorHighlighted,
  ),
  buttonEmptyCorneredBackgroundHighlightedHovered: deriveHovered(
    EMPTY_CORNERED_PARAMS_HIGHLIGHTED.buttonEmptyCorneredBackgroundHighlighted,
  ),
};

/** ButtonStyle.EMPTY_ROUNDED */
const EMPTY_ROUNDED_PARAMS = {
  buttonEmptyRoundedFontStyle: createStylesParam('empty-rounded-font-style', {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
      return { ...font } as IWixStyleFont;
    },
  }),
  buttonEmptyRoundedWidth: createStylesParam('empty-rounded-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 262,
  }),
  buttonEmptyRoundedAlignment: createStylesParam('empty-rounded-alignment', {
    type: StyleParamType.Number,
    getDefaultValue: () => Alignment.CENTER,
  }),
  buttonEmptyRoundedFontColor: createStylesParam('empty-rounded-font-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstButtonPrimaryColor),
    },
  }),
  buttonEmptyRoundedBorderWidth: createStylesParam('empty-rounded-border-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  }),
  buttonEmptyRoundedCornerRadius: createStylesParam('empty-rounded-corner-radius', {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
  }),
  buttonEmptyRoundedBorderOpacityAndColor: createStylesParam('empty-rounded-border-opacity-and-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstButtonPrimaryBorderLeftColor),
    },
  }),
  buttonEmptyRoundedBackground: createStylesParam('empty-rounded-bg-opacity-and-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstButtonPrimaryBackgroundColor),
    },
  }),
};

const EMPTY_ROUNDED_PARAMS_HIGHLIGHTED = {
  buttonEmptyRoundedFontColorHighlighted: deriveHighlighted(EMPTY_ROUNDED_PARAMS.buttonEmptyRoundedFontColor),
  buttonEmptyRoundedCornerRadiusHighlighted: deriveHighlighted(EMPTY_ROUNDED_PARAMS.buttonEmptyRoundedCornerRadius),
  buttonEmptyRoundedBorderOpacityAndColorHighlighted: deriveHighlighted(
    EMPTY_ROUNDED_PARAMS.buttonEmptyRoundedBorderOpacityAndColor,
  ),
  buttonEmptyRoundedBackgroundHighlighted: deriveHighlighted(EMPTY_ROUNDED_PARAMS.buttonEmptyRoundedBackground),
};

const EMPTY_ROUNDED_PARAMS_HOVERED = {
  buttonEmptyRoundedFontColorHovered: deriveHovered(EMPTY_ROUNDED_PARAMS.buttonEmptyRoundedFontColor),
  buttonEmptyRoundedBorderOpacityAndColorHovered: deriveHovered(
    EMPTY_ROUNDED_PARAMS.buttonEmptyRoundedBorderOpacityAndColor,
  ),
  buttonEmptyRoundedBackgroundHovered: deriveHovered(EMPTY_ROUNDED_PARAMS.buttonEmptyRoundedBackground),

  buttonEmptyRoundedFontColorHighlightedHovered: deriveHovered(
    EMPTY_ROUNDED_PARAMS_HIGHLIGHTED.buttonEmptyRoundedFontColorHighlighted,
  ),
  buttonEmptyRoundedBorderOpacityAndColorHighlightedHovered: deriveHovered(
    EMPTY_ROUNDED_PARAMS_HIGHLIGHTED.buttonEmptyRoundedBorderOpacityAndColorHighlighted,
  ),
  buttonEmptyRoundedBackgroundHighlightedHovered: deriveHovered(
    EMPTY_ROUNDED_PARAMS_HIGHLIGHTED.buttonEmptyRoundedBackgroundHighlighted,
  ),
};

/** ButtonType.SECONDARY */
const SECONDARY_BUTTON_PARAMS = {
  secondaryButtonFontStyle: createStylesParam('secondary-button-font-style', {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
      return { ...font } as IWixStyleFont;
    },
  }),
  secondaryButtonWidth: createStylesParam('secondary-button-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 262,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 262,
      },
    },
  }),
  secondaryButtonAlignment: createStylesParam('secondary-button-alignment', {
    type: StyleParamType.Number,
    getDefaultValue: () => Alignment.CENTER,
    metadata: {
      type: StyleType.justifyContent,
      defaultValue: {
        type: 'static',
        value: 'center',
      },
    },
  }),
  secondaryButtonFontColor: createStylesParam('secondary-button-font-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstButtonPrimaryColor),
    },
  }),
  secondaryButtonBorderOpacityAndColor: createStylesParam('secondary-button-border-opacity-and-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstButtonPrimaryBorderLeftColor),
    },
  }),
  secondaryButtonBorderWidth: createStylesParam('secondary-button-border-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  secondaryButtonCornerRadius: createStylesParam('secondary-button-corner-radius', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  secondaryButtonBackground: createStylesParam('secondary-button-bg-opacity-and-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstButtonPrimaryBackgroundColor),
    },
  }),
};

const SECONDARY_BUTTON_PARAMS_HIGHLIGHTED = {
  secondaryButtonFontColorHighlighted: deriveHighlighted(SECONDARY_BUTTON_PARAMS.secondaryButtonFontColor),
  secondaryButtonCornerRadiusHighlighted: deriveHighlighted(SECONDARY_BUTTON_PARAMS.secondaryButtonCornerRadius),
  secondaryButtonBorderOpacityAndColorHighlighted: deriveHighlighted(
    SECONDARY_BUTTON_PARAMS.secondaryButtonBorderOpacityAndColor,
  ),
  secondaryButtonBackgroundHighlighted: deriveHighlighted(SECONDARY_BUTTON_PARAMS.secondaryButtonBackground),
};

const SECONDARY_BUTTON_PARAMS_HOVERED = {
  secondaryButtonFontColorHovered: deriveHovered(SECONDARY_BUTTON_PARAMS.secondaryButtonFontColor),
  secondaryButtonBorderOpacityAndColorHovered: deriveHovered(
    SECONDARY_BUTTON_PARAMS.secondaryButtonBorderOpacityAndColor,
  ),
  secondaryButtonBackgroundHovered: deriveHovered(SECONDARY_BUTTON_PARAMS.secondaryButtonBackground),
  secondaryButtonFontColorHighlightedHovered: deriveHovered(
    SECONDARY_BUTTON_PARAMS_HIGHLIGHTED.secondaryButtonFontColorHighlighted,
  ),
  secondaryButtonBorderOpacityAndColorHighlightedHovered: deriveHovered(
    SECONDARY_BUTTON_PARAMS_HIGHLIGHTED.secondaryButtonBorderOpacityAndColorHighlighted,
  ),
  secondaryButtonBackgroundHighlightedHovered: deriveHovered(
    SECONDARY_BUTTON_PARAMS_HIGHLIGHTED.secondaryButtonBackgroundHighlighted,
  ),
};

export const buttonsStylesParams = {
  ...FULL_CORNERED_PARAMS,
  ...FULL_CORNERED_PARAMS_HIGHLIGHTED,
  ...FULL_CORNERED_PARAMS_HOVERED,

  ...FULL_ROUNDED_PARAMS,
  ...FULL_ROUNDED_PARAMS_HIGHLIGHTED,
  ...FULL_ROUNDED_PARAMS_HOVERED,

  ...EMPTY_CORNERED_PARAMS,
  ...EMPTY_CORNERED_PARAMS_HIGHLIGHTED,
  ...EMPTY_CORNERED_PARAMS_HOVERED,

  ...EMPTY_ROUNDED_PARAMS,
  ...EMPTY_ROUNDED_PARAMS_HIGHLIGHTED,
  ...EMPTY_ROUNDED_PARAMS_HOVERED,

  ...SECONDARY_BUTTON_PARAMS,
  ...SECONDARY_BUTTON_PARAMS_HIGHLIGHTED,
  ...SECONDARY_BUTTON_PARAMS_HOVERED,
};
